/**
 * Admin-only Review summary poller for the pending counter.
 *
 * Deliberately NO WebSocket / realtime infra: a lightweight refresh strategy is
 * enough for a moderation control room —
 *   • fetch on mount (once admin is confirmed)
 *   • refresh on window focus / tab visibility return / hash navigation
 *   • a modest poll (12s) ONLY while an elevated admin session is visible
 *   • an imperative `refresh()` fired after publish/review/moderation actions
 * All errors fail soft (the badge simply keeps its last known value).
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { reviewApi, type ReviewSummary } from "../../lib/review-api";

const POLL_MS = 12000;

export function useReviewSummary(enabled: boolean): { summary: ReviewSummary | null; refresh: () => void } {
  const [summary, setSummary] = useState<ReviewSummary | null>(null);
  const enabledRef = useRef(enabled);
  enabledRef.current = enabled;

  const load = useCallback((signal?: AbortSignal) => {
    if (!enabledRef.current) return;
    reviewApi
      .summary(signal)
      .then((s) => setSummary(s))
      .catch(() => {});
  }, []);

  const refresh = useCallback(() => load(), [load]);

  useEffect(() => {
    if (!enabled) return;
    const controller = new AbortController();
    load(controller.signal);

    const bg = () => load();
    const onVisible = () => {
      if (document.visibilityState === "visible") load();
    };
    window.addEventListener("focus", bg);
    window.addEventListener("hashchange", bg);
    document.addEventListener("visibilitychange", onVisible);
    const timer = window.setInterval(() => {
      if (document.visibilityState !== "hidden") load();
    }, POLL_MS);

    return () => {
      controller.abort();
      window.removeEventListener("focus", bg);
      window.removeEventListener("hashchange", bg);
      document.removeEventListener("visibilitychange", onVisible);
      window.clearInterval(timer);
    };
  }, [enabled, load]);

  return { summary, refresh };
}
